import React, { useMemo } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  AppBarProps,
  useTheme,
  Box,
  useMediaQuery,
} from "@mui/material";
import { Text } from "@/components/atoms/Text";
import { BaseMoleculeProps } from "@/components/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export interface HeaderProps
  extends Omit<AppBarProps, "variant">,
    BaseMoleculeProps {
  /**
   * Header title
   */
  title?: string;

  /**
   * Left side content or back button
   */
  leftContent?: React.ReactNode;

  /**
   * Right side content (actions, menu)
   */
  rightContent?: React.ReactNode;

  /**
   * Show back button
   */
  showBackButton?: boolean;

  /**
   * Back button click handler
   */
  onBackClick?: () => void;

  /**
   * Header variant
   */
  variant?: "default" | "compact" | "expanded";

  /**
   * Transparent background
   */
  transparent?: boolean;

  /**
   * Fixed position
   */
  fixed?: boolean;

  /**
   * Loading state
   */
  loading?: boolean;
}

// Static height mapping - moved outside component for performance
const HEIGHT_MAP = {
  compact: { xs: "40px", md: "46px" },
  default: { xs: "56px", md: "64px" },
  expanded: { xs: "64px", md: "72px" },
} as const;

const HeaderComponent: React.FC<HeaderProps> = ({
  title,
  leftContent,
  rightContent,
  showBackButton = false,
  onBackClick,
  variant = "default",
  transparent = false,
  fixed = false,
  loading = false,
  className,
  "data-testid": dataTestId,
  children,
  ...props
}) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Memoized toolbar styles to prevent recreation on every render
  const toolbarSx = useMemo(
    () => ({
      height: "100%",
      minHeight: "unset !important",
      paddingX: {
        xs: 2,
        md: 3,
      },
      gap: {
        xs: 1,
        md: 2,
      },
    }),
    []
  );

  // Memoized AppBar styles
  const appBarSx = useMemo(
    () => ({
      // Mobile-first responsive styling
      height: HEIGHT_MAP[variant],
      minHeight: HEIGHT_MAP[variant],

      // Transparent background with theme integration
      ...(transparent && {
        backgroundColor: "transparent",
        backdropFilter: "blur(8px)",
      }),

      // Loading state using theme colors
      ...(loading && {
        opacity: 0.7,
        pointerEvents: "none",
      }),
    }),
    [variant, transparent, loading, theme.palette.divider]
  );

  // Memoized back button to prevent recreation
  const backButton = useMemo(
    () =>
      showBackButton && (
        <IconButton edge="start" color="inherit" onClick={onBackClick}>
          <ArrowBackIcon
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : theme.palette.common.black,
            }}
          />
        </IconButton>
      ),
    [showBackButton, onBackClick, isMobile]
  );

  // Memoized title component
  const titleComponent = useMemo(
    () =>
      title && (
        <Text
          variant="h6"
          sx={{
            flexGrow: 1,
            fontSize: {
              xs: 14,
              md: 14,
            },
            fontWeight: 600,
            letterSpacing: 1.4,
            color: theme.palette.text.primary,
            textAlign: {
              xs: "center",
              md: "left",
            },
          }}
        >
          {title}
        </Text>
      ),
    [title, leftContent, showBackButton, theme.palette.text.primary]
  );

  const appBarProps: AppBarProps = {
    position: fixed ? "fixed" : "static",
    elevation: transparent ? 0 : 2,
    className,
    sx: appBarSx,
    ...props,
  };

  return (
    <AppBar {...appBarProps}>
      <Toolbar sx={toolbarSx}>
        {/* Left side content */}
        {isMobile && (
          <Box sx={{ minWidth: 40, minHeight: 40 }}>
            {leftContent || backButton}
          </Box>
        )}

        {/* Title */}
        {titleComponent}

        {/* Custom content */}
        {children && <div style={{ flexGrow: 1 }}>{children}</div>}

        {/* Right side content */}
        <Box sx={{ minWidth: 40, minHeight: 40 }}>{rightContent}</Box>
      </Toolbar>
    </AppBar>
  );
};

// Simple memoization for performance optimization
export const Header = React.memo(HeaderComponent);

// Set display name for debugging
Header.displayName = "Header";
